import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';

interface EquipmentDetail {
  name: string;
  spec: string;
  description: string;
  bgColor: string;
  buttonStyle: string;
  imageUrl: string;
  detailTitle: string;
  detailDesc: string;
  features: string[];
  applications: string[];
}

export default function ProductionEquipmentPage() {
  const navigate = useNavigate();
  const [selectedEquipment, setSelectedEquipment] = useState<EquipmentDetail | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [mobileEquipmentOpen, setMobileEquipmentOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);

  const handleGoToSection = (sectionId: string) => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const navLinks = [
    { href: '/#about', label: '회사소개', type: 'dropdown', subMenu: [
      { href: null, label: '회사소개', scrollTo: 'about' },
      { href: '/history', label: '연혁', scrollTo: null },
      { href: '/organization', label: '조직도', scrollTo: null },
      { href: null, label: '오시는 길', scrollTo: 'map' },
    ]},
    { href: null, label: '생산공정', type: 'section', scrollTo: 'process' },
    { href: '#equipment', label: '보유장비', type: 'dropdown', subMenu: [
      { href: '/equipment/production', label: '생산장비', scrollTo: null },
      { href: '/equipment/welding', label: '용접장비', scrollTo: null },
      { href: '/equipment/capability', label: '가공능력', scrollTo: null },
      { href: '/equipment/materials', label: '가공 가능자재', scrollTo: null },
    ]},
    { href: '#products', label: '제조물품', type: 'dropdown', subMenu: [
      { href: '/gallery', label: '갤러리', scrollTo: null },
    ]},
  ];

  const equipment: EquipmentDetail[] = [
    {
      name: 'Smart Fiber',
      spec: '6KW 파이버 레이저',
      description: '고정밀 가공 가능',
      bgColor: 'bg-blue-50',
      buttonStyle: 'bg-white text-gray-900 border-2 border-gray-900 hover:bg-gray-900 hover:text-white',
      imageUrl: 'https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/c4a92ab87c6c2ee2af9568029b655551.jpeg',
      detailTitle: 'Smart Fiber 6KW 파이버 레이저 절단기',
      detailDesc: '최신형 6KW 파이버 레이저 절단기로, 고출력 레이저 빔을 통해 금속 판재를 초고속·초정밀로 절단합니다. CO2 레이저 대비 전천후 다양한 소재나 두께에도 유연하게 대응이 가능하며, 폭넓은 가공 범위를 자랑합니다.',
      features: [
        '최대 출력 6,000W 파이버 레이저',
        '절단 정밀도 ±0.1mm 이하',
        '스테인리스·철판·알루미늄 전 소재 대응',
        '자동 노즐 교환 및 포커스 조정 시스템',
        '다른 기계보다 빠르고 간단하게 작업이 가능',
        '최대 가공 사이즈 3,048 × 1,524mm',
      ],
      applications: ['정밀 부품 제작', '대량 생산 절단', '복잡한 형상 가공', '얇은 박판 고속 절단'],
    },
    {
      name: 'BYSTRONIC 3015 PRO',
      spec: 'CO2 레이저',
      description: '대형 판재 가공',
      bgColor: 'bg-gray-100',
      buttonStyle: 'bg-white text-gray-900 border-2 border-gray-900 hover:bg-gray-900 hover:text-white',
      imageUrl: 'https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/da91f43dab91993e48704a7463d3338e.jpeg',
      detailTitle: 'BYSTRONIC 3015 PRO CO2 레이저 절단기',
      detailDesc: 'BYSTRONIC 3015 PRO는 대형 판재 가공에 최적화된 CO2 레이저 절단기입니다. 넓은 가공 영역과 안정적인 절단 품질로 다양한 소재의 대형 부품 제작에 탁월한 성능을 발휘합니다. 두꺼운 철판 절단에 특히 강점을 보입니다.',
      features: [
        '가공 영역 3,048 × 1,524mm 대형 사이즈',
        '두꺼운 철판 절단 가능',
        '고출력 CO2 레이저 안정적 절단',
        '자동 소재 감지 및 높이 조절',
        '절단면 품질 우수, 후처리 최소화',
      ],
      applications: ['대형 구조물 부품', '두꺼운 철판 절단', '건축·산업용 패널', '중장비 부품 제작'],
    },
    {
      name: 'NCT 가공 장비',
      spec: 'PEGA-357 Amada',
      description: '다양한 형상 가공',
      bgColor: 'bg-gray-100',
      buttonStyle: 'bg-white text-gray-900 border-2 border-gray-900 hover:bg-gray-900 hover:text-white',
      imageUrl: 'https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/69eae2760a437859672b8df1a2c37402.png',
      detailTitle: 'NCT 터렛 펀칭 머신 (PEGA-357 Amada)',
      detailDesc: '일본 Amada사의 PEGA-357 NCT(수치제어 터렛 펀칭) 장비로, 다양한 형상의 홀 가공 및 성형 작업을 고속·정밀하게 수행합니다. 레이저 절단과 병행하여 복잡한 형상의 부품도 효율적으로 제작할 수 있습니다.',
      features: [
        '최대 가공 사이즈 1,270 × 2,540mm',
        '분당 최대 300회 타발 고속 가공',
        '다양한 형상 금형 자동 교환',
        '홀 가공·루버·버링·엠보싱 등 성형 가능',
      ],
      applications: ['다수 홀 가공 부품', '루버·버링 성형', '전기 판넬 제작', '복잡한 형상 부품', '배수용 타공판 및 바닥 타공판'],
    },
  ];

  return (
    <>
      <SEOHead
        title="생산장비 | NS-TECH 판금가공"
        description="NS-TECH의 고성능 레이저 절단 장비. Bystronic Smart Fiber 6KW 파이버 레이저, CO2 레이저, NCT 가공 장비로 정밀도와 생산성을 보장합니다."
        keywords="레이저 절단기, 파이버 레이저, CO2 레이저, NCT 가공, BYSTRONIC, 판금 장비, 정밀 가공"
        ogType="website"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'NS-TECH 생산장비',
            description: '고성능 레이저 절단 및 NCT 가공 장비',
            url: `${import.meta.env.VITE_SITE_URL}/equipment/production`,
            itemListElement: equipment.map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'Product',
                name: item.name,
                description: item.detailDesc,
              },
            })),
          },
        ]}
      />
      <div className="min-h-screen bg-white">
        {/* 네비게이션 */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
          <div className="w-full px-4 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="hidden lg:flex items-center gap-2">
                <Link to="/" className="flex items-center gap-2 shrink-0 mr-2" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}>
                  <img
                    src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                    alt="NS-TECH Logo"
                    className="h-12 w-auto object-contain"
                  />
                </Link>
                {navLinks.map((link) => (
                  link.type === 'dropdown' ? (
                    <div
                      key={link.label}
                      className="relative"
                      onMouseEnter={() => {
                        if (link.label === '보유장비') setEquipmentDropdownOpen(true);
                        if (link.label === '제조물품') setProductsDropdownOpen(true);
                        if (link.label === '회사소개') setAboutDropdownOpen(true);
                      }}
                      onMouseLeave={() => {
                        if (link.label === '보유장비') setEquipmentDropdownOpen(false);
                        if (link.label === '제조물품') setProductsDropdownOpen(false);
                        if (link.label === '회사소개') setAboutDropdownOpen(false);
                      }}
                    >
                      <button className={`text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md flex items-center gap-1 cursor-pointer ${
                        link.label === '보유장비' ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
                      }`}>
                        {link.label}
                        <i className={`ri-arrow-down-s-line transition-transform ${
                          (link.label === '보유장비' && equipmentDropdownOpen) ||
                          (link.label === '제조물품' && productsDropdownOpen) ||
                          (link.label === '회사소개' && aboutDropdownOpen) ? 'rotate-180' : ''
                        }`}></i>
                      </button>
                      {((link.label === '보유장비' && equipmentDropdownOpen) ||
                        (link.label === '제조물품' && productsDropdownOpen) ||
                        (link.label === '회사소개' && aboutDropdownOpen)) && (
                        <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-lg py-2 min-w-[160px] border border-gray-100">
                          {link.subMenu?.map((subLink) => (
                            subLink.scrollTo ? (
                              <button
                                key={subLink.label}
                                onClick={() => handleGoToSection(subLink.scrollTo!)}
                                className="block w-full text-left px-4 py-2 text-sm hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                              >
                                {subLink.label}
                              </button>
                            ) : (
                              <Link
                                key={subLink.href}
                                to={subLink.href!}
                                className={`block px-4 py-2 text-sm hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap ${
                                  subLink.href === '/equipment/production' ? 'text-orange-500 font-semibold' : 'text-gray-700'
                                }`}
                              >
                                {subLink.label}
                              </Link>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  ) : link.type === 'section' ? (
                    <button
                      key={link.label}
                      onClick={() => handleGoToSection((link as any).scrollTo)}
                      className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50 cursor-pointer"
                    >
                      {link.label}
                    </button>
                  ) : (
                    <Link key={link.href!} to={link.href!}
                      className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50">
                      {link.label}
                    </Link>
                  )
                ))}
              </div>
              <div className="flex lg:hidden items-center justify-between w-full">
                <Link to="/" className="flex items-center gap-2 shrink-0" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}>
                  <img src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png" alt="NS-TECH Logo" className="h-10 w-auto object-contain" />
                </Link>
                <button
                  className="flex flex-col justify-center items-center w-10 h-10 gap-1.5 cursor-pointer relative z-[60]"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="메뉴 열기"
                >
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                </button>
              </div>
            </div>
          </div>
          <div className={`lg:hidden fixed inset-0 bg-black/50 transition-opacity duration-300 z-40 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={() => setMobileMenuOpen(false)}></div>
          <div className={`lg:hidden fixed top-[60px] left-0 right-0 transition-all duration-300 ease-in-out z-50 ${mobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'}`}>
            <div className="bg-white shadow-xl border-t border-gray-100 px-5 py-3 flex flex-col gap-1">
              {navLinks.map((link) => (
                link.type === 'dropdown' ? (
                  <div key={link.label}>
                    <button
                      onClick={() => {
                        if (link.label === '보유장비') setMobileEquipmentOpen(!mobileEquipmentOpen);
                        if (link.label === '제조물품') setMobileProductsOpen(!mobileProductsOpen);
                        if (link.label === '회사소개') setMobileAboutOpen(!mobileAboutOpen);
                      }}
                      className="w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50 flex items-center justify-between"
                    >
                      {link.label}
                      <i className={`ri-arrow-down-s-line transition-transform ${
                        (link.label === '보유장비' && mobileEquipmentOpen) ||
                        (link.label === '제조물품' && mobileProductsOpen) ||
                        (link.label === '회사소개' && mobileAboutOpen) ? 'rotate-180' : ''
                      }`}></i>
                    </button>
                    {((link.label === '보유장비' && mobileEquipmentOpen) ||
                      (link.label === '제조물품' && mobileProductsOpen) ||
                      (link.label === '회사소개' && mobileAboutOpen)) && (
                      <div className="pl-4 mt-1 space-y-1">
                        {link.subMenu?.map((subLink) => (
                          subLink.scrollTo ? (
                            <button
                              key={subLink.label}
                              onClick={() => { setMobileMenuOpen(false); handleGoToSection(subLink.scrollTo!); }}
                              className="block w-full text-left text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50"
                            >
                              {subLink.label}
                            </button>
                          ) : (
                            <Link key={subLink.href} to={subLink.href!} onClick={() => setMobileMenuOpen(false)}
                              className="block text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50">
                              {subLink.label}
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ) : link.type === 'section' ? (
                  <button
                    key={link.label}
                    onClick={() => { setMobileMenuOpen(false); handleGoToSection((link as any).scrollTo); }}
                    className="text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                  >
                    {link.label}
                  </button>
                ) : (
                  <Link key={link.href!} to={link.href!} onClick={() => setMobileMenuOpen(false)}
                    className="text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-gray-700 hover:text-orange-500 hover:bg-orange-50">
                    {link.label}
                  </Link>
                )
              ))}
            </div>
          </div>
        </nav>

        {/* 페이지 헤더 */}
        <div className="pt-24 pb-12 px-5 md:px-20 bg-gradient-to-br from-orange-50 to-white">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3">생산장비</h1>
            <p className="text-base md:text-lg text-gray-600">정밀도와 생산성을 보장하는 고성능 레이저 절단 장비</p>
          </div>
        </div>

        {/* 장비 목록 */}
        <section className="py-16 md:py-20 px-5 md:px-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7">
              {equipment.map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                  <div className={`${item.bgColor} rounded-xl h-48 md:h-56 mb-5 overflow-hidden`}>
                    <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover object-center" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-500 text-center mb-1">{item.name}</h3>
                  <p className="text-lg md:text-xl font-bold text-gray-900 text-center mb-4">{item.spec}</p>
                  <button
                    onClick={() => setSelectedEquipment(item)}
                    className={`w-4/5 mx-auto block py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${item.buttonStyle}`}
                  >
                    상세보기
                  </button>
                  <p className="text-xs text-gray-400 text-center mt-3">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 장비 상세 모달 */}
        {selectedEquipment && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={() => setSelectedEquipment(null)}>
            <div className="bg-white rounded-t-3xl md:rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="relative w-full h-52 md:h-64 overflow-hidden rounded-t-3xl">
                <img src={selectedEquipment.imageUrl} alt={selectedEquipment.name} className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <button onClick={() => setSelectedEquipment(null)} className="absolute top-4 right-4 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors cursor-pointer shadow">
                  <i className="ri-close-line text-gray-700 text-lg"></i>
                </button>
                <div className="absolute bottom-4 left-5">
                  <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">{selectedEquipment.spec}</span>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">{selectedEquipment.detailTitle}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-6">{selectedEquipment.detailDesc}</p>
                <div className="mb-6">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-1 h-4 bg-orange-500 rounded-full inline-block"></span>
                    주요 사양 및 특징
                  </h4>
                  <ul className="space-y-2.5">
                    {selectedEquipment.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-orange-500 mt-0.5 shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-1 h-4 bg-gray-800 rounded-full inline-block"></span>
                    주요 적용 분야
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedEquipment.applications.map((app, i) => (
                      <span key={i} className="bg-gray-100 text-gray-700 text-xs font-medium px-3 py-1.5 rounded-full">{app}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 푸터 */}
        <footer className="bg-gray-900 text-white py-12 px-5 md:px-20">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-sm text-gray-400">© 2024 NS-TECH. All rights reserved.</p>
          </div>
        </footer>

        {/* 모바일 플로팅 전화 버튼 */}
        <a
          href="tel:031-541-1902"
          className="lg:hidden fixed bottom-24 right-4 z-50 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white pl-3 pr-4 py-2.5 rounded-full shadow-xl transition-all active:scale-95 cursor-pointer"
          aria-label="전화 연결"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-phone-fill text-white text-base"></i>
          </div>
          <span className="text-xs font-bold whitespace-nowrap">전화문의</span>
        </a>
      </div>
    </>
  );
}
